import { CommonModule } from '@angular/common';

import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    OnDestroy,
    ViewChild,
    inject,
    signal,
} from '@angular/core';

import {
    DomSanitizer,
    SafeResourceUrl,
} from '@angular/platform-browser';

import {
    MAT_DIALOG_DATA,
    MatDialogRef,
} from '@angular/material/dialog';

import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

export interface PdfPreviewDialogData {
    title: string;
    fileName?: string;
    blob: Blob;
}

@Component({
    selector: 'app-pdf-preview-dialog',
    standalone: true,

    imports: [
        CommonModule,
        MatIconModule,
        MatProgressSpinnerModule,
    ],

    templateUrl:
        './pdf-preview-dialog.component.html',

    styleUrl:
        './pdf-preview-dialog.component.scss',

    changeDetection:
        ChangeDetectionStrategy.OnPush,
})
export class PdfPreviewDialogComponent
    implements AfterViewInit, OnDestroy {

    protected readonly data =
        inject<PdfPreviewDialogData>(
            MAT_DIALOG_DATA
        );

    private readonly dialogRef =
        inject(
            MatDialogRef<
                PdfPreviewDialogComponent,
                void
            >
        );

    private readonly sanitizer =
        inject(DomSanitizer);

    @ViewChild('mobilePdfContainer')
    private mobilePdfContainer?:
        ElementRef<HTMLDivElement>;

    private readonly objectUrl =
        URL.createObjectURL(
            this.data.blob
        );

    protected readonly previewUrl:
        SafeResourceUrl =
        this.sanitizer
            .bypassSecurityTrustResourceUrl(
                this.objectUrl
            );

    protected readonly useCanvasPreview =
        this.shouldUseCanvasPreview();

    protected readonly useNativeAppleFileSave =
        this.isAppleMobileDevice();
    protected readonly isPdfRendering =
        signal(false);

    protected readonly pdfRenderError =
        signal<string | null>(null);

    private destroyed = false;

    private renderRunId = 0;

    ngAfterViewInit(): void {
        if (!this.useCanvasPreview) {
            return;
        }

        void this.renderPdfPages();
    }

    ngOnDestroy(): void {
        this.destroyed = true;

        this.renderRunId++;

        URL.revokeObjectURL(
            this.objectUrl
        );
    }

    protected async downloadPdf(): Promise<void> {
        const fileName =
            this.data.fileName ??
            'Bestelluebersicht.pdf';

        /*
         * Safari on iPhone/iPad can open blob URLs as a document
         * instead of reliably downloading them.
         *
         * On Apple mobile devices we therefore use the native
         * share/save sheet. The user can explicitly choose
         * "In Dateien sichern" / "Save to Files".
         */
        if (this.useNativeAppleFileSave) {
            const file =
                new File(
                    [this.data.blob],
                    fileName,
                    {
                        type:
                            this.data.blob.type ||
                            'application/pdf',
                    }
                );

            const canShareFiles =
                typeof navigator.share ===
                'function' &&
                (
                    typeof navigator.canShare !==
                    'function' ||
                    navigator.canShare({
                        files: [file],
                    })
                );

            if (canShareFiles) {
                try {
                    await navigator.share({
                        files: [file],
                        title: fileName,
                    });

                    return;
                } catch (error) {
                    /*
                     * The user simply closed the native share
                     * dialog. Do not fall back to opening a
                     * blob page in this case.
                     */
                    if (
                        error instanceof DOMException &&
                        error.name === 'AbortError'
                    ) {
                        return;
                    }

                    console.warn(
                        'Native PDF save failed, falling back to browser download:',
                        error
                    );
                }
            }
        }

        this.triggerBrowserDownload(
            fileName
        );
    }

    protected close(): void {
        this.dialogRef.close();
    }

    private isAppleMobileDevice(): boolean {
        const userAgent =
            navigator.userAgent;

        const classicAppleMobile =
            /iPad|iPhone|iPod/i.test(
                userAgent
            );

        /*
         * Modern iPadOS can identify itself as macOS.
         * maxTouchPoints differentiates it from a real Mac.
         */
        const modernIPad =
            navigator.platform ===
            'MacIntel' &&
            navigator.maxTouchPoints > 1;

        return (
            classicAppleMobile ||
            modernIPad
        );
    }

    private triggerBrowserDownload(
        fileName: string
    ): void {
        const link =
            document.createElement('a');

        link.href =
            this.objectUrl;

        link.download =
            fileName;

        document.body.appendChild(
            link
        );

        link.click();

        link.remove();
    }

    private shouldUseCanvasPreview(): boolean {
        return (
            navigator.maxTouchPoints > 0 ||
            window.matchMedia(
                '(pointer: coarse)'
            ).matches ||
            window.innerWidth <= 1024
        );
    }

    private async renderPdfPages(): Promise<void> {
        const container =
            this.mobilePdfContainer
                ?.nativeElement;

        if (!container) {
            return;
        }

        const currentRunId =
            ++this.renderRunId;

        this.isPdfRendering.set(true);
        this.pdfRenderError.set(null);

        container.replaceChildren();

        try {
            /*
             * Wait until the dialog/container has its
             * final width before calculating the page scale.
             */
            await new Promise<void>(
                (resolve) => {
                    requestAnimationFrame(
                        () => resolve()
                    );
                }
            );

            /*
             * Load PDF.js only when the custom preview is
             * actually needed. Desktop keeps using the
             * browser's native iframe PDF viewer.
             */
            const pdfjs =
                await import(
                    'pdfjs-dist/legacy/build/pdf.mjs'
                );

            pdfjs.GlobalWorkerOptions.workerSrc =
                new URL(
                    'assets/pdfjs/4.10.38/pdf.worker.min.mjs',
                    document.baseURI
                ).toString();

            const pdfData =
                new Uint8Array(
                    await this.data.blob.arrayBuffer()
                );

            if (
                this.destroyed ||
                currentRunId !== this.renderRunId
            ) {
                return;
            }

            const loadingTask =
                pdfjs.getDocument({
                    data: pdfData,
                });

            try {
                const pdf =
                    await loadingTask.promise;

                for (
                    let pageNumber = 1;
                    pageNumber <= pdf.numPages;
                    pageNumber++
                ) {
                    if (
                        this.destroyed ||
                        currentRunId !== this.renderRunId
                    ) {
                        return;
                    }

                    const page =
                        await pdf.getPage(
                            pageNumber
                        );

                    const baseViewport =
                        page.getViewport({
                            scale: 1,
                        });

                    const availableWidth =
                        Math.max(
                            container.clientWidth,
                            1
                        );

                    const cssScale =
                        availableWidth /
                        baseViewport.width;

                    /*
                     * Limit the pixel density to avoid
                     * excessive memory usage on iPhones
                     * and iPads with high-DPI screens.
                     */
                    const outputScale =
                        Math.min(
                            window.devicePixelRatio || 1,
                            1.5
                        );

                    const cssViewport =
                        page.getViewport({
                            scale: cssScale,
                        });

                    const renderViewport =
                        page.getViewport({
                            scale:
                                cssScale *
                                outputScale,
                        });

                    const pageWrapper =
                        document.createElement(
                            'div'
                        );

                    pageWrapper.style.width =
                        '100%';

                    pageWrapper.style.display =
                        'flex';

                    pageWrapper.style.justifyContent =
                        'center';

                    pageWrapper.style.alignItems =
                        'flex-start';

                    pageWrapper.style.marginBottom =
                        pageNumber === pdf.numPages
                            ? '0'
                            : '12px';

                    const canvas =
                        document.createElement(
                            'canvas'
                        );

                    canvas.width =
                        Math.floor(
                            renderViewport.width
                        );

                    canvas.height =
                        Math.floor(
                            renderViewport.height
                        );

                    canvas.style.width =
                        `${Math.floor(
                            cssViewport.width
                        )}px`;

                    canvas.style.height =
                        `${Math.floor(
                            cssViewport.height
                        )}px`;

                    canvas.style.maxWidth =
                        '100%';

                    canvas.style.display =
                        'block';

                    canvas.style.background =
                        '#ffffff';

                    pageWrapper.appendChild(
                        canvas
                    );

                    container.appendChild(
                        pageWrapper
                    );

                    const context =
                        canvas.getContext('2d');

                    if (!context) {
                        throw new Error(
                            'Canvas context could not be created.'
                        );
                    }

                    const renderTask =
                        page.render({
                            canvasContext: context,
                            viewport: renderViewport,
                        });

                    await renderTask.promise;

                    page.cleanup();

                    /*
                     * As soon as page 1 is ready,
                     * remove the loading indicator.
                     * Remaining pages continue rendering
                     * underneath it.
                     */
                    if (
                        pageNumber === 1 &&
                        !this.destroyed
                    ) {
                        this.isPdfRendering.set(
                            false
                        );
                    }
                }
            } finally {
                await loadingTask.destroy();
            }
        } catch (error) {
            console.error(
                'PDF preview rendering failed:',
                error
            );

            if (
                !this.destroyed &&
                currentRunId === this.renderRunId
            ) {
                this.pdfRenderError.set(
                    'Die PDF-Vorschau konnte nicht geladen werden.'
                );
            }
        } finally {
            if (
                !this.destroyed &&
                currentRunId === this.renderRunId
            ) {
                this.isPdfRendering.set(
                    false
                );
            }
        }
    }
}